import { useMsal } from '@azure/msal-react';
import { Button, makeStyles, mergeClasses, Option, Tab, TabList, Text, Tooltip } from '@fluentui/react-components';
import parse from 'html-react-parser';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { downloadFile } from '../../common/file/download-file';
import { useCommonStyles, useStores } from '../../common/hooks';
import { isAiEvalTabEnabled } from '../../configurations';
import { getCleanHtmlString } from '../../common/html-string/get-clean-html-string';
import { TooltipFieldName } from '../../common/tooltip/tooltip-field-name';
import { getTooltipContentByFieldName } from '../../common/tooltip/tooltip-get-content';
import { AwardJudge, AwardLead, AwardNominationReviewer, AwardNominationSummary, blankAwardLead, blankAwardNominationReviewer, NominationRole } from '../../models';
import { ResourceTypes } from '../../models/resource-services';
import { AwardRouteParams } from '../../models/route-params';
import { RoutePaths } from '../../route-paths';
import { PageTitle } from '../shared-components';
import { JudgeAssignmentModal } from '../shared-components/JudgeAssignmentModal';
import Loading from '../shared-components/Loading';
import { AssignWinnerSection } from '../shared-components/assign-winner-section/AssignWinnerSection';
import { FlexBox } from '../shared-components/flex-box';
import { SelectOptions } from '../shared-components/form';
import { LeadAssignment } from '../shared-components/lead-assignment/LeadAssignment';
import './AwardPage.scss';
import { AwardTabTypeCode } from './award-page-ui-store';
import { AwardNominationSummaryTable } from './components/AwardNominationSummaryTable';
import { AwardStatisticSection } from './components/AwardStatisticSection';
import { EvaluationComparisonPanel } from './components/EvaluationComparisonPanel';

const useStyles = makeStyles({
  root: {
    alignItems: 'center',
  },
});

const tabDisplayNames: Record<AwardTabTypeCode, string> = {
  [AwardTabTypeCode.Nominations]: 'Nominations',
  [AwardTabTypeCode.Details]: 'Details',
  [AwardTabTypeCode.AiEval]: 'AI Eval',
};

const tabTooltipFieldNames: Partial<Record<AwardTabTypeCode, TooltipFieldName>> = {
  [AwardTabTypeCode.Nominations]: 'nominationTab',
  [AwardTabTypeCode.Details]: 'detailsTab',
};

const AwardPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    userStore,
    awardStore,
    awardPageUiStore
  } = useStores();

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);

  const { awardId } = useParams<AwardRouteParams>();

  const onAssignAwardLead = async (emailAddress: string, isPrimary: boolean): Promise<void> => {
    setIsLoading(true);
    const newAwardLead = blankAwardLead();
    newAwardLead.emailAddress = emailAddress;
    newAwardLead.awardId = Number(awardId);
    newAwardLead.isPrimary = isPrimary;

    try {
      await apiStore.assignAwardLeads(instance, Number(awardId), newAwardLead);
      await getAwardLeads(Number(awardId));
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteAwardLead = async (awardLead: AwardLead): Promise<void> => {
    setIsLoading(true);
    try {
      await apiStore.deleteAwardLeads(instance, awardLead.awardLeadId);
      await getAwardLeads(Number(awardId));
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteAwardNominationReviewer = async (awardNominationReviewer: AwardNominationReviewer): Promise<void> => {
    setIsLoading(true);
    try {
      await apiStore.deleteAwardNominationReviewer(instance, awardNominationReviewer.awardNominationReviewerId);
      await getAwardNominationReviewers(Number(awardId));
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const onAssignAwardNominationReviewer = async (emailAddress: string): Promise<void> => {
    setIsLoading(true);
    const newAwardNominationReviewer = blankAwardNominationReviewer();
    newAwardNominationReviewer.emailAddress = emailAddress;
    newAwardNominationReviewer.awardId = Number(awardId);

    try {
      await apiStore.assignAwardNominationReviewers(
        instance,
        Number(awardId),
        newAwardNominationReviewer
      );
      await getAwardNominationReviewers(Number(awardId));
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddJudges = (emails: string[]): void => {
    awardPageUiStore.addPendingAwardJudges(emails, Number(awardId));
  }

  const handleDismissJudgeAssignmentModal = async (): Promise<void> => {
    setIsLoading(true);
    try {
      awardPageUiStore.setShowJudgeAssignmentModal(false);
      awardPageUiStore.clearPendingAwardJudges();
      await getAwardJudges(Number(awardId));
    } finally {
      setIsLoading(false);
    }
  }

  const handleSubmitJudgeAssignmentModal = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const assignedJudges = await Promise.all(
        awardPageUiStore.pendingAwardJudges.map(async (judge) => {
          const judgeRef = await apiStore.assignAwardJudge(instance, Number(awardId), judge);
          return {
            ...judge,
            awardJudgeId: judgeRef.awardJudgeId,
          };
        })
      );
      awardPageUiStore.addAwardJudges(assignedJudges);
      awardPageUiStore.clearPendingAwardJudges();
      awardPageUiStore.setShowJudgeAssignmentModal(false);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteJudge = async (judge: AwardJudge): Promise<void> => {
    if (judge.awardJudgeId === 0) {
      awardPageUiStore.removePendingAwardJudge(judge);
      return;
    }

    setIsLoading(true);
    try {
      await apiStore.deleteAwardJudge(instance, judge.awardJudgeId);
      awardPageUiStore.removeAwardJudge(judge);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleUpdateJudgeStatus = async (judge: AwardJudge, status: boolean): Promise<void> => {
    if (judge.awardJudgeId === 0) {
      awardPageUiStore.setPendingAwardJudgeStatus(judge, status);
      return;
    }
    setIsLoading(true);
    try {
      await apiStore.updateAwardJudgeStatus(instance, judge.awardJudgeId, status);
      awardPageUiStore.setAwardJudgeStatus(judge, status);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleOpenJudgeAssignmentModal = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await getAwardJudges(Number(awardId));
      awardPageUiStore.setShowJudgeAssignmentModal(true);
    } finally {
      setIsLoading(false);
    }
  }

  const handleSelectedAwardNominationSummary = async (nominationSummary: AwardNominationSummary): Promise<void> => {
    setIsLoading(true);
    try {
      awardPageUiStore.setSelectedAwardNominationSummary(nominationSummary);
      await getAwardNominationRole(nominationSummary.nominationId);
    } finally {
      setIsLoading(false);
    }
  }

  const handleUpdateAwardNominationRole = async (nominationRole: NominationRole): Promise<void> => {
    setIsLoading(true);
    try {
      await updateAwardNominationRole(nominationRole);
      await getAwardNominationSummaries(Number(awardId));
      awardPageUiStore.setSelectedAwardNominationSummary(undefined);
      awardPageUiStore.setSelectedNominationRole(undefined);
    } finally {
      setIsLoading(false);
    }
  }

  const updateAwardConfiguration = async (awardId: number, newValue: number): Promise<void> => {
    setIsLoading(true);

    try {
      await apiStore.updateAwardConfiguration(instance, awardId, {
        judgeCapacity: newValue,
      });
      const award = await apiStore.getAwardById(instance, awardId);
      awardPageUiStore.setSelectedAward(award);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const downloadAwardExportedNominationsSummaries = async (awardId: number, selectedFields: string[], query: string): Promise<void> => {
    try {
      const resourceTicket = await apiStore.postResourceTicket(instance, {
        data: {
          routeData: {
            awardId: awardId,
          },
          queryString: {
            selectedFields: selectedFields.join(','),
            searchText: query ? query : undefined,
          }
        },
        resourceType: ResourceTypes.ExportedAwardNominationSummaries,
      });
      downloadFile(resourceTicket.ticketId);
    } catch (error) {
      // TODO - handle error
    }
  }

  const isDisableAwardLeadAssignment = (): boolean => {
    return !(userStore.isAdmin || userStore.isGroupLead);
  }

  const isDisableAwardNominationReviewerAssignment = (): boolean => {
    return !(userStore.isAdmin || userStore.isGroupLead || userStore.isAwardLead);
  }

  const canAssignWinnersAndFinalists = (): boolean => {
    return (
      (awardStore.isReviewAndDesignateWindowOpen && (
        userStore.isAdmin ||
        userStore.isGroupLead ||
        userStore.isAwardLead
      )) ||
      (awardStore.isComplianceAndRevisionsWindowOpen && userStore.isAdmin)
    );
  };

  const getAwardById = async (awardId: number): Promise<void> => {
    try {
      const award = await apiStore.getAwardById(instance, awardId);
      awardPageUiStore.setSelectedAward(award);
    } catch (error) {
      setIsNotFound(true);
    }
  };

  const getAwardJudgeCapacityOptions = async (awardId: number): Promise<void> => {
    try {
      const judgeCapacityOptions = await apiStore.getAwardJudgeCapacityOptions(instance, awardId);
      awardPageUiStore.setJudgeCapacityOptions(judgeCapacityOptions);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardNominationSummaries = async (awardId: number): Promise<void> => {
    try {
      const nominationRefs = await apiStore.getAwardNominationSummaries(instance, awardId);
      awardPageUiStore.setAwardNominationSummaries(nominationRefs);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardStatistic = async (awardId: number): Promise<void> => {
    try {
      const awardStatistic = await apiStore.getAwardStatistic(instance, awardId);
      awardPageUiStore.setAwardStatistic(awardStatistic);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardLeads = async (awardId: number): Promise<void> => {
    try {
      const awardLeads = await apiStore.getAwardLeads(instance, awardId);
      awardPageUiStore.setAwardLeads(awardLeads);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardNominationReviewers = async (awardId: number): Promise<void> => {
    try {
      const awardNominationReviewers = await apiStore.getAwardNominationReviewers(instance, awardId);
      awardPageUiStore.setAwardNominationReviewers(awardNominationReviewers);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardJudges = async (awardId: number): Promise<void> => {
    try {
      const awardJudges = await apiStore.getAwardJudges(instance, awardId);
      awardPageUiStore.setAwardJudges(awardJudges);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardNominationRole = async (nominationId: number): Promise<void> => {
    try {
      const nominationRole = await apiStore.getAwardNominationRole(instance, Number(awardId), nominationId);
      awardPageUiStore.setSelectedNominationRole(nominationRole);
    } catch (error) {
      // TODO - handle error
    }
  };

  const updateAwardNominationRole = async (nominationRole: NominationRole): Promise<void> => {
    try {
      await apiStore.updateAwardNominationRole(instance, Number(awardId), nominationRole.nominationId, nominationRole);
    } catch (error) {
      // TODO - handle error
    }
  };

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);

    if (!awardId) {
      setIsLoading(false);
      return;
    }

    const currentAwardId = Number(awardId);

    await getAwardById(currentAwardId);

    await Promise.allSettled([
      getAwardNominationSummaries(currentAwardId),
      getAwardStatistic(currentAwardId),
      getAwardLeads(currentAwardId),
      getAwardNominationReviewers(currentAwardId),
      getAwardJudges(currentAwardId),
      getAwardJudgeCapacityOptions(currentAwardId),
    ]);

    setIsLoading(false);
  }

  useEffect(() => {
    preloadData();
    return () => {
      awardPageUiStore.resetStates();
    }
  }, [awardId]);

  if (isNotFound) {
    return (
      <Navigate
        to={RoutePaths.NotFound}
        replace
      />
    )
  }

  const showAiEvalTab = isAiEvalTabEnabled && (userStore.isAdmin || userStore.isGroupLead || userStore.isAwardLead || userStore.isAwardNominationReviewer);

  const visibleAwardTabTypeCodes = showAiEvalTab
    ? [...awardPageUiStore.activeAwardTabTypeCodes, AwardTabTypeCode.AiEval]
    : awardPageUiStore.activeAwardTabTypeCodes;

  const renderAwardTab = (tabTypeCode: AwardTabTypeCode, index: number): React.JSX.Element => {
    const tooltipFieldName = tabTooltipFieldNames[tabTypeCode];
    const tab = <Tab value={tabTypeCode}>{tabDisplayNames[tabTypeCode]}</Tab>;
    return (
      <React.Fragment key={tabTypeCode}>
        {
          index > 0 &&
          <Text weight='bold'>|</Text>
        }
        {
          tooltipFieldName
            ? (
              <Tooltip
                withArrow
                relationship='description'
                content={{
                  children: getTooltipContentByFieldName('staff-award-page', tooltipFieldName) ?? '',
                  className: commonStyles.tooltip,
                }}
              >
                {tab}
              </Tooltip>
            )
            : tab
        }
      </React.Fragment>
    );
  };

  return (
    <FlexBox
      direction='column'
      content='start'
      className='award-page'
    >
      {
        isLoading && <Loading />
      }
      <JudgeAssignmentModal
        isModalOpen={awardPageUiStore.isShowJudgeAssignmentModal}
        activeJudgesCount={awardPageUiStore.activeJudgesCount}
        inactiveJudgesCount={awardPageUiStore.inactiveJudgesCount}
        addedJudges={awardPageUiStore.pendingAwardJudges}
        judges={awardPageUiStore.awardJudges}
        onAddJudges={handleAddJudges}
        onSubmitModal={handleSubmitJudgeAssignmentModal}
        onDismissModal={handleDismissJudgeAssignmentModal}
        onDeleteJudge={handleDeleteJudge}
        onUpdateJudgeStatus={handleUpdateJudgeStatus}
      />
      {
        awardPageUiStore.selectedAward && (
          <>
            <PageTitle
              title={awardPageUiStore.selectedAward.title}
              subtitle={awardStore.defaultProgramRef?.displayName}
            />
            <TabList
              size='large'
              className={mergeClasses('award-page__tab-list', styles.root)}
              selectedValue={awardPageUiStore.selectedAwardTabTypeCode}
              onTabSelect={(_, data) =>
                awardPageUiStore.setSelectedAwardTabTypeCode(data.value as AwardTabTypeCode)
              }
            >
              {
                visibleAwardTabTypeCodes.map((tabTypeCode, index) => renderAwardTab(tabTypeCode, index))
              }
            </TabList>
            {
              awardPageUiStore.selectedAwardTabTypeCode === AwardTabTypeCode.Nominations && (
                <FlexBox
                  isFullWidth
                  className='award-page__main award-page__main--nominations'
                >
                  <AwardNominationSummaryTable
                    awardId={awardPageUiStore.selectedAward.awardId}
                    canAssignWinnersAndFinalists={canAssignWinnersAndFinalists()}
                    downloadAwardExportedNominationsSummaries={(awardId, query, selectedFields) => downloadAwardExportedNominationsSummaries(awardId, selectedFields, query)}
                    setSelectedAwardNominationSummary={handleSelectedAwardNominationSummary}
                  />
                  {
                    canAssignWinnersAndFinalists() && (
                      <AssignWinnerSection
                        isWinnerAssigned={awardPageUiStore.isWinnerAssigned}
                        isFinalistAssigned={awardPageUiStore.isFinalistAssigned}
                        maxWinners={awardPageUiStore.selectedAward.maxWinners}
                        maxFinalists={awardPageUiStore.selectedAward.maxFinalists}
                        selectedNominationSummary={awardPageUiStore.selectedAwardNominationSummary}
                        selectedNominationRole={awardPageUiStore.selectedNominationRole}
                        handleUpdateAwardNominationRole={handleUpdateAwardNominationRole}
                      />
                    )
                  }
                  {
                    !canAssignWinnersAndFinalists() && !awardStore.isWinnersWindowOpen && (
                      <AwardStatisticSection />
                    )
                  }
                </FlexBox>
              )
            }
            {
              awardPageUiStore.selectedAwardTabTypeCode === AwardTabTypeCode.Details && (
                (
                  <FlexBox
                    className='award-page__main award-page__main--details'
                  >
                    <div className={mergeClasses(commonStyles.richTextContent, 'details__summary')}>
                      {parse(getCleanHtmlString(awardPageUiStore.selectedAward?.summary || ''))}
                    </div>
                    <FlexBox
                      direction='column'
                      className='details__assignments'
                    >
                      <LeadAssignment
                        leads={awardPageUiStore.awardLeads}
                        title='Primary award lead'
                        isPrimaryLead={true}
                        allowMultipleEmailAddresses={false}
                        onAddLead={emailAddress => onAssignAwardLead(emailAddress, true)}
                        onDeleteLead={lead => onDeleteAwardLead(lead as AwardLead)}
                        disabled={isDisableAwardLeadAssignment()}
                        titleTooltipText={getTooltipContentByFieldName('staff-award-page', 'primaryAwardLeadTitle')}
                      />
                      <LeadAssignment
                        leads={awardPageUiStore.awardLeads}
                        title='Award assistants'
                        isPrimaryLead={false}
                        allowMultipleEmailAddresses={false}
                        onAddLead={emailAddress => onAssignAwardLead(emailAddress, false)}
                        onDeleteLead={lead => onDeleteAwardLead(lead as AwardLead)}
                        disabled={isDisableAwardLeadAssignment()}
                        titleTooltipText={getTooltipContentByFieldName('staff-award-page', 'awardAssistantsTitle')}
                      />
                      <LeadAssignment
                        leads={awardPageUiStore.awardNominationReviewers}
                        title='Nomination Review Team Members'
                        isPrimaryLead={false}
                        allowMultipleEmailAddresses={false}
                        onAddLead={emailAddress => onAssignAwardNominationReviewer(emailAddress)}
                        onDeleteLead={nominationReviewer => onDeleteAwardNominationReviewer(nominationReviewer as AwardNominationReviewer)}
                        disabled={isDisableAwardNominationReviewerAssignment()}
                      />
                      {
                        (
                          userStore.isAdmin ||
                          userStore.isGroupLead ||
                          userStore.isAwardLead
                        ) && (
                          <SelectOptions
                            label='Judge Capacity'
                            tooltipText='Select the maximum number of nominations 1 judge can evaluate.'
                            onOptionSelect={(data) => {
                              if (!data.optionValue) return;
                              updateAwardConfiguration(Number(awardId), Number(data.optionValue));
                            }}
                            dropdownProps={{
                              value: awardPageUiStore.selectedJudgeCapacityValue,
                              selectedOptions: awardPageUiStore.selectedJudgeCapacityOptions,
                              disabled: !awardPageUiStore.judgeCapacityOptions.length,
                            }}
                          >
                            {
                              awardPageUiStore.judgeCapacityOptions.map((option) => (
                                <Option
                                  key={option.value}
                                  value={String(option.value)}
                                >
                                  {option.text}
                                </Option>
                              ))
                            }
                          </SelectOptions>
                        )
                      }
                      <FlexBox
                        aligns='start'
                        content='space-between'
                      >
                        <FlexBox>
                          <Text weight='semibold'>
                            {'Judges ('}
                          </Text>
                          <Tooltip
                            withArrow
                            relationship='description'
                            content={{
                              children: getTooltipContentByFieldName('staff-award-page', 'activeJudges') ?? '',
                              className: commonStyles.tooltip,
                            }}
                          >
                            <Text
                              className='text--green'
                            >
                              {`${awardPageUiStore.activeJudgesCount} active`}
                            </Text>
                          </Tooltip>
                          <Tooltip
                            withArrow
                            relationship='description'
                            content={{
                              children: getTooltipContentByFieldName('staff-award-page', 'inactiveJudges') ?? '',
                              className: commonStyles.tooltip,
                            }}
                          >
                            <Text>{` / ${awardPageUiStore.inactiveJudgesCount} inactive)`}</Text>
                          </Tooltip>
                        </FlexBox>
                        <Button
                          appearance='primary'
                          onClick={handleOpenJudgeAssignmentModal}
                          disabled={!(
                            userStore.isAdmin ||
                            userStore.isGroupLead ||
                            userStore.isAwardLead
                          )}
                        >
                          View / Edit
                        </Button>
                      </FlexBox>
                    </FlexBox>
                  </FlexBox>
                )
              )
            }
            {
              awardPageUiStore.selectedAwardTabTypeCode === AwardTabTypeCode.AiEval && showAiEvalTab && (
                <FlexBox
                  isFullWidth
                  className='award-page__main'
                >
                  <EvaluationComparisonPanel awardId={Number(awardId)} />
                </FlexBox>
              )
            }
          </>
        )
      }
      {
        !awardPageUiStore.selectedAward && (
          <Text
            block
            align='center'
            as='p'
          >
            Sorry, no award is currently selected.
          </Text>
        )
      }
    </FlexBox>
  );
}

export const AwardPage = observer(AwardPageImpl);
